import React, { useEffect, useState, useMemo, useCallback } from "react";
import { db, auth } from "@/config/firebase";
import { collection, getDocs, doc, setDoc, getDoc, deleteDoc, onSnapshot, serverTimestamp, query, where, orderBy, addDoc, updateDoc } from "firebase/firestore";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ChallengeGameMaster } from "@/components/challenges/ChallengeGameMaster";
import { ChallengeLiveTools } from "@/components/challenges/ChallengeLive";
import { ArrowLeft, Play, Pause, SkipForward, Clock, Users, Volume2, VolumeX, Maximize, RotateCcw, Plus, Trash2, Settings, CheckCircle, XCircle, Eye, AlertTriangle, Trophy } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CURRENT_SEASON = "2026-27";

// Session State Machine - Extended for full game flow
const PHASES = {
  // PHASE A - Predictions
  LOBBY: 'LOBBY',
  IDENTITY_GUESS: 'IDENTITY_GUESS',
  IDENTITY_LOCKED: 'IDENTITY_LOCKED',
  IDENTITY_REVEAL: 'IDENTITY_REVEAL',
  PREDICTION_1_VOTING: 'PREDICTION_1_VOTING',
  PREDICTION_1_REVEAL: 'PREDICTION_1_REVEAL',
  PREDICTION_2_VOTING: 'PREDICTION_2_VOTING',
  PREDICTION_2_REVEAL: 'PREDICTION_2_REVEAL',
  USER_COMPLETE: 'USER_COMPLETE',
  PREDICTIONS_COMPLETE: 'PREDICTIONS_COMPLETE',  // NEW: After all authors done
  
  // PHASE B - Challenge Games
  CHALLENGE_GAME_REVEAL: 'CHALLENGE_GAME_REVEAL',
  CHALLENGE_PLAYER_DRAW: 'CHALLENGE_PLAYER_DRAW',
  CHALLENGE_BETTING_OPEN: 'CHALLENGE_BETTING_OPEN',
  CHALLENGE_BETTING_LOCKED: 'CHALLENGE_BETTING_LOCKED',
  CHALLENGE_PLAYING: 'CHALLENGE_PLAYING',
  CHALLENGE_RESULT: 'CHALLENGE_RESULT',
  CHALLENGES_COMPLETE: 'CHALLENGES_COMPLETE',
  
  // PHASE C - Season Market
  SEASON_MARKET_OPEN: 'SEASON_MARKET_OPEN',
  
  // Final
  SESSION_COMPLETE: 'SESSION_COMPLETE',
};

// Helper: Make authenticated API call with Firebase ID Token
async function apiCall(endpoint, method = 'GET', body = null) {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('Nicht eingeloggt');
  }
  
  const token = await currentUser.getIdToken();
  
  const options = {
    method,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
  
  if (body && method !== 'GET') {
    options.body = JSON.stringify(body);
  }
  
  const response = await fetch(`${BACKEND_URL}${endpoint}`, options);
  const result = await response.json();
  
  if (!response.ok) {
    throw new Error(result.detail || `HTTP ${response.status}`);
  }
  
  return result;
}

export default function PredictionsHost() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const { sessionId } = useParams();
  
  // Data
  const [session, setSession] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [presentations, setPresentations] = useState({});
  const [predictions, setPredictions] = useState([]);
  const [users, setUsers] = useState([]);
  const [guesses, setGuesses] = useState([]);
  const [votes, setVotes] = useState([]);
  
  // Phase B: Challenge State
  const [currentChallengeRound, setCurrentChallengeRound] = useState(null);
  const [challengeBets, setChallengeBets] = useState([]);
  const [availableChallenges, setAvailableChallenges] = useState([]);
  
  // UI State
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(null);
  
  // Audio – der Host spielt NIE selbst; alles läuft ausschließlich auf der Stage (Firestore audioState)
  const [volumeDraft, setVolumeDraft] = useState(null);
  const [stageAlive, setStageAlive] = useState(false);

  // Create session modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSessionName, setNewSessionName] = useState('Season Opening 2026/27');
  const [availableSessions, setAvailableSessions] = useState([]);
  const [wallets, setWallets] = useState({});
  const [playedChallengeIds, setPlayedChallengeIds] = useState([]);
  useEffect(() => {
    if (!sessionId) return;
    const unsub = onSnapshot(query(collection(db, "challenge_rounds"), where("sessionId", "==", sessionId)), (snap) => {
      setPlayedChallengeIds(snap.docs.map(d => d.data().challengeId).filter(Boolean));
    });
    return () => unsub();
  }, [sessionId]);
  const [coinEdit, setCoinEdit] = useState({ userId: null, value: '' });
  const [coinBusy, setCoinBusy] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    const unsub = onSnapshot(collection(db, "prediction_wallets"), (snap) => {
      const map = {};
      snap.docs.forEach(d => { map[d.id] = d.data(); });
      setWallets(map);
    });
    return () => unsub();
  }, [sessionId]);

  const adjustCoins = async (userId, delta) => {
    if (!delta) return;
    setCoinBusy(true);
    try {
      await apiCall('/api/economy/admin/adjust-wallet', 'POST', {
        user_id: userId, delta, session_id: sessionId,
        reason: `Spielleiter-Korrektur ${delta > 0 ? '+' : ''}${delta}`,
      });
    } catch (err) {
      alert(`Coins konnten nicht geändert werden: ${err.message}`);
    }
    setCoinBusy(false);
  };

  const setSecondaryPlayers = async (userIds) => {
    try {
      await apiCall('/api/challenges/set-secondary-players', 'POST', { session_id: sessionId, round_id: currentChallengeRound?.id, user_ids: userIds });
    } catch (err) {
      alert(`Auswahl fehlgeschlagen: ${err.message}`);
    }
  };

  const startPhaseTwoNow = async () => {
    const pool = (session?.activeParticipantIds?.length ? session.activeParticipantIds : participants.map(p => p.userId || p.oduid)).filter(Boolean);
    if (!window.confirm(`Phase 2 (Mini-Games & Wetten) jetzt starten?\n${pool.length} Spieler im Challenge-Pool. Die Prediction-Runden werden damit beendet.`)) return;
    await setDoc(doc(db, "prediction_sessions", sessionId), {
      phase: 'CHALLENGE_LOBBY',
      phaseStartedAt: serverTimestamp(),
      phaseEndsAt: null,
      challengeParticipantIds: pool,
      challengedUserIds: session?.challengedUserIds || [],
    }, { merge: true });
  };
  const [deletingSessionId, setDeletingSessionId] = useState(null);
  const [resettingWallets, setResettingWallets] = useState(false);

  const deleteSession = async (sess) => {
    if (!window.confirm(`Session „${sess.name}“ wirklich löschen?\nAlle Teilnehmer, Antworten und Votes dieser Session werden entfernt.`)) return;
    setDeletingSessionId(sess.id);
    try {
      await apiCall(`/api/economy/admin/sessions/${sess.id}`, 'DELETE');
      setAvailableSessions(prev => prev.filter(s => s.id !== sess.id));
    } catch (err) {
      alert(`Löschen fehlgeschlagen: ${err.message}`);
    }
    setDeletingSessionId(null);
  };

  const openStageWindowFor = (id) => {
    const w = window.screen?.availWidth || 1920;
    const h = window.screen?.availHeight || 1080;
    window.open(
      `/predictions/stage/${id}`,
      `rheinstars-stage-${id}`,
      `popup=yes,width=${w},height=${h},left=0,top=0,menubar=no,toolbar=no,location=no,status=no`
    );
  };
  const openStageWindow = () => openStageWindowFor(sessionId);

  // Foyer-Video von der Steuerung aus vor/zurück schalten (die Stage hört auf session.foyerCmd)
  const foyerVideoCmd = async (action) => {
    try {
      await updateDoc(doc(db, "prediction_sessions", sessionId), { foyerCmd: { action, nonce: Date.now() } });
    } catch (e) { console.error("foyerCmd failed", e); }
  };

  const resetLeaderboard = async () => {
    if (!window.confirm('Rangliste zurücksetzen?\nALLE Coins werden auf 0 gesetzt und alle Coin-Transaktionen gelöscht. Das kann nicht rückgängig gemacht werden.')) return;
    setResettingWallets(true);
    try {
      const res = await apiCall('/api/economy/admin/reset-wallets', 'POST');
      alert(`Rangliste geleert: ${res.walletsReset} Wallets auf 0, ${res.transactionsDeleted} Transaktionen gelöscht.`);
    } catch (err) {
      alert(`Reset fehlgeschlagen: ${err.message}`);
    }
    setResettingWallets(false);
  };
  
  // NEW: User selection for session creation
  const [showUserSelection, setShowUserSelection] = useState(false);
  const [showGamesManager, setShowGamesManager] = useState(false);
  const [readyUsers, setReadyUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [loadingReadyUsers, setLoadingReadyUsers] = useState(false);

  // Phase B: Challenge Pool editing
  const [showChallengePoolEditor, setShowChallengePoolEditor] = useState(false);
  const [challengePoolSelection, setChallengePoolSelection] = useState(new Set());
  const [bettingTimerSeconds, setBettingTimerSeconds] = useState(600);
  const [bettingCountdown, setBettingCountdown] = useState(null);

  // Load users and presentations
  useEffect(() => {
    if (role !== 'admin') return;
    
    const loadStaticData = async () => {
      try {
        // Users
        const usersSnap = await getDocs(collection(db, "users"));
        setUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        
        // Presentations
        const presSnap = await getDocs(
          query(collection(db, "prediction_presentations"), where("seasonId", "==", CURRENT_SEASON))
        );
        const presData = {};
        presSnap.docs.forEach(d => {
          const data = d.data();
          presData[data.userId] = { id: d.id, ...data };
        });
        setPresentations(presData);
        
        // Predictions
        const predSnap = await getDocs(
          query(collection(db, "season_predictions"), where("seasonId", "==", CURRENT_SEASON))
        );
        setPredictions(predSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // Available sessions - fetch without orderBy to avoid needing composite index
        // Sort in memory instead
        try {
          const sessSnap = await getDocs(
            query(collection(db, "prediction_sessions"), where("seasonId", "==", CURRENT_SEASON))
          );
          const sessions = sessSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          // Sort by createdAt desc in memory
          sessions.sort((a, b) => {
            const aTime = a.createdAt?.toMillis?.() || 0;
            const bTime = b.createdAt?.toMillis?.() || 0;
            return bTime - aTime;
          });
          setAvailableSessions(sessions);
        } catch (sessError) {
          console.error("Error loading sessions:", sessError);
          setAvailableSessions([]);
        }
      } catch (error) {
        console.error("Error loading static data:", error);
      }
    };
    
    loadStaticData();
  }, [role]);

  // Subscribe to session updates
  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      return;
    }
    
    const unsubSession = onSnapshot(
      doc(db, "prediction_sessions", sessionId),
      (snap) => {
        if (snap.exists()) {
          setSession({ id: snap.id, ...snap.data() });
        }
        setLoading(false);
      },
      (error) => {
        console.error("Session snapshot error:", error);
        setLoading(false);
      }
    );
    
    // Participants
    const unsubParticipants = onSnapshot(
      query(collection(db, "prediction_session_participants"), where("sessionId", "==", sessionId)),
      (snap) => {
        setParticipants(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      },
      (error) => {
        console.error("Participants snapshot error:", error);
      }
    );
    
    return () => {
      unsubSession();
      unsubParticipants();
    };
  }, [sessionId]);

  // Subscribe to predictions for realtime updates (teamProbability, quote after finalize)
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "season_predictions"), where("seasonId", "==", CURRENT_SEASON)),
      (snap) => {
        setPredictions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }
    );
    
    return () => unsub();
  }, []);

  // Subscribe to current challenge round (Phase B)
  // Uses Firestore onSnapshot with API fallback on permission errors
  useEffect(() => {
    if (!session?.currentChallengeRoundId) {
      setCurrentChallengeRound(null);
      return;
    }
    
    const roundId = session.currentChallengeRoundId;
    console.log('[PredictionsHost] Subscribing to challenge round:', roundId);
    
    let pollInterval = null;
    let usePolling = false;
    
    // API fallback polling function
    const pollRoundData = async () => {
      try {
        const result = await apiCall(`/api/challenges/round/${roundId}`);
        if (result && result.id) {
          console.log('[PredictionsHost] API poll success:', result);
          setCurrentChallengeRound(result);
        }
      } catch (err) {
        console.error('[PredictionsHost] API poll error:', err);
      }
    };
    
    // Try Firestore subscription first
    const unsub = onSnapshot(
      doc(db, "challenge_rounds", roundId),
      (snap) => {
        console.log('[PredictionsHost] Challenge round snapshot:', snap.exists(), snap.id);
        if (snap.exists()) {
          const data = { id: snap.id, ...snap.data() };
          console.log('[PredictionsHost] Challenge round data:', data);
          setCurrentChallengeRound(data);
          // If we were polling, stop it
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
        } else {
          console.warn('[PredictionsHost] Challenge round doc does not exist:', roundId);
          // Try API fallback
          pollRoundData();
        }
      },
      (error) => {
        console.error('[PredictionsHost] Challenge round subscription error:', error);
        // Switch to API polling on permission errors
        if (!usePolling) {
          usePolling = true;
          console.log('[PredictionsHost] Switching to API polling due to Firestore error');
          pollRoundData(); // Initial fetch
          pollInterval = setInterval(pollRoundData, 2000); // Poll every 2 seconds
        }
      }
    );
    
    return () => {
      unsub();
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [session?.currentChallengeRoundId]);

  // Subscribe to challenge bets for current round
  useEffect(() => {
    if (!session?.currentChallengeRoundId) {
      setChallengeBets([]);
      return;
    }
    
    const unsub = onSnapshot(
      query(
        collection(db, "challenge_bets"),
        where("roundId", "==", session.currentChallengeRoundId)
      ),
      (snap) => {
        setChallengeBets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }
    );
    
    return () => unsub();
  }, [session?.currentChallengeRoundId]);

  // Betting countdown timer
  useEffect(() => {
    if (!currentChallengeRound?.bettingEndsAt) {
      setBettingCountdown(null);
      return;
    }
    
    const updateCountdown = () => {
      const endsAt = currentChallengeRound.bettingEndsAt.toDate?.() || 
                     new Date(currentChallengeRound.bettingEndsAt);
      const remaining = Math.max(0, Math.floor((endsAt - Date.now()) / 1000));
      setBettingCountdown(remaining);
      
      // Auto-lock if timer expired (trigger backend check)
      if (remaining === 0 && currentChallengeRound.phase === 'BETTING_OPEN') {
        apiCall('/api/challenges/auto-lock-expired', 'POST', {}).catch(console.error);
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 500);
    
    return () => clearInterval(interval);
  }, [currentChallengeRound?.bettingEndsAt, currentChallengeRound?.phase]);

  // Initialize challenge pool selection when session loads
  useEffect(() => {
    if (session?.challengeParticipantIds) {
      setChallengePoolSelection(new Set(session.challengeParticipantIds));
    } else if (session?.activeParticipantIds) {
      setChallengePoolSelection(new Set(session.activeParticipantIds));
    }
  }, [session?.challengeParticipantIds, session?.activeParticipantIds]);

  // Subscribe to guesses for current round
  useEffect(() => {
    if (!session?.currentUserId || !sessionId) return;
    
    const unsubGuesses = onSnapshot(
      query(
        collection(db, "prediction_identity_guesses"),
        where("sessionId", "==", sessionId),
        where("roundUserId", "==", session.currentUserId)
      ),
      (snap) => {
        setGuesses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      },
      (error) => {
        console.error("Guesses snapshot error:", error);
      }
    );
    
    return () => unsubGuesses();
  }, [session?.currentUserId, sessionId]);

  // Subscribe to votes for current prediction
  useEffect(() => {
    if (!session?.currentPredictionId || !sessionId) return;
    
    const unsubVotes = onSnapshot(
      query(
        collection(db, "prediction_probability_votes"),
        where("sessionId", "==", sessionId),
        where("predictionId", "==", session.currentPredictionId)
      ),
      (snap) => {
        setVotes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      },
      (error) => {
        console.error("Votes snapshot error:", error);
      }
    );
    
    return () => unsubVotes();
  }, [session?.currentPredictionId, sessionId]);

  // Countdown timer
  useEffect(() => {
    if (!session?.phaseEndsAt) {
      setCountdown(null);
      return;
    }
    
    const updateCountdown = () => {
      const endsAt = session.phaseEndsAt.toDate?.() || new Date(session.phaseEndsAt);
      const remaining = Math.max(0, Math.floor((endsAt - Date.now()) / 1000));
      setCountdown(remaining);
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(interval);
  }, [session?.phaseEndsAt]);

  // Get ordered user list for session
  const sessionUserOrder = useMemo(() => {
    if (!session?.userOrder) return [];
    return session.userOrder.map(uid => {
      const user = users.find(u => u.id === uid);
      const pres = presentations[uid];
      return { uid, user, presentation: pres };
    }).filter(u => u.user && u.presentation);
  }, [session?.userOrder, users, presentations]);

  // Current user data
  const currentUserData = useMemo(() => {
    if (!session?.currentUserId) return null;
    const user = users.find(u => u.id === session.currentUserId);
    const pres = presentations[session.currentUserId];
    const userPreds = predictions.filter(p => p.ownerUserId === session.currentUserId);
    return { user, presentation: pres, predictions: userPreds };
  }, [session?.currentUserId, users, presentations, predictions]);

  // Current prediction data
  const currentPrediction = useMemo(() => {
    if (!session?.currentPredictionId) return null;
    return predictions.find(p => p.id === session.currentPredictionId);
  }, [session?.currentPredictionId, predictions]);

  // Create new session (via backend with settings snapshot)
  const createSession = async () => {
    if (!newSessionName.trim()) return;
    
    // If no users selected yet, load ready users and show selection
    if (!showUserSelection) {
      await loadReadyUsers();
      setShowUserSelection(true);
      return;
    }
    
    // Validate selection
    if (selectedUserIds.size === 0) {
      alert('Bitte mindestens einen Spieler auswählen');
      return;
    }
    
    setLoading(true);
    
    try {
      // Use backend API that enforces settings snapshot and centralized READY check
      const result = await apiCall('/api/predictions/create-session', 'POST', {
        name: newSessionName.trim(),
        season_id: CURRENT_SEASON,
        test_mode: false,
        selected_user_ids: Array.from(selectedUserIds),  // NEW: Pass selected users
      });
      
      if (result.ok) {
        console.log(`Session created with ${result.user_count} users`);
        console.log('Settings snapshot:', result.settings);
        setShowUserSelection(false);
        setSelectedUserIds(new Set());
        navigate(`/admin/predictions/session/${result.session_id}`);
      } else {
        const errorMsg = typeof result.error === 'string' ? result.error : 
                        result.detail || result.message || JSON.stringify(result);
        alert(errorMsg);
      }
    } catch (err) {
      console.error('Create session error:', err);
      const errorMsg = err.message || err.detail || 'Fehler beim Erstellen der Session';
      alert(errorMsg);
    }
    
    setLoading(false);
  };
  
  // Load READY users from backend
  const loadReadyUsers = async () => {
    setLoadingReadyUsers(true);
    try {
      const result = await apiCall(`/api/predictions/ready-users/${CURRENT_SEASON}`, 'GET');
      if (result.users) {
        const ready = result.users.filter(u => u.status === 'READY');
        setReadyUsers(ready);
        // Pre-select all ready users
        setSelectedUserIds(new Set(ready.map(u => u.user_id)));
      }
    } catch (err) {
      console.error('Failed to load ready users:', err);
      alert('Fehler beim Laden der Spieler');
    }
    setLoadingReadyUsers(false);
  };
  
  // Toggle user selection
  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };
  
  // Select/deselect all
  const selectAllUsers = () => {
    setSelectedUserIds(new Set(readyUsers.map(u => u.user_id)));
  };
  
  const deselectAllUsers = () => {
    setSelectedUserIds(new Set());
  };
  
  // Create test session (single user mode for superadmins)
  const createTestSession = async () => {
    if (!newSessionName.trim()) return;
    
    setLoading(true);
    
    try {
      const result = await apiCall('/api/predictions/create-session', 'POST', {
        name: `[TEST] ${newSessionName.trim()}`,
        season_id: CURRENT_SEASON,
        test_mode: true,  // Single-user test mode
      });
      
      if (result.ok) {
        console.log(`TEST Session created with ${result.user_count} user(s)`);
        navigate(`/admin/predictions/session/${result.session_id}`);
      } else {
        alert(result.error || 'Fehler beim Erstellen der Test-Session');
      }
    } catch (err) {
      console.error('Create test session error:', err);
      alert(err.message || 'Fehler beim Erstellen der Test-Session');
    }
    
    setLoading(false);
  };

  // Phase transition helpers
  const updatePhase = async (phase, additionalData = {}) => {
    if (!sessionId) return;
    
    await setDoc(doc(db, "prediction_sessions", sessionId), {
      phase,
      phaseStartedAt: serverTimestamp(),
      ...additionalData,
    }, { merge: true });
  };

  const setPhaseTimer = async (seconds) => {
    if (!sessionId) return;
    
    const endsAt = new Date(Date.now() + seconds * 1000);
    await setDoc(doc(db, "prediction_sessions", sessionId), {
      phaseEndsAt: endsAt,
    }, { merge: true });
  };

  // ATTENDANCE LOCK - Locks the participant list and grants starting coins
  const lockAttendance = async () => {
    if (!sessionId || !session) return;
    
    try {
      // Get current participant IDs (prefer userId over legacy oduid)
      const activeIds = participants.map(p => p.userId || p.oduid).filter(Boolean);
      
      if (activeIds.length === 0) {
        if (!window.confirm('Noch kein Spieler verbunden.\nTestlauf ohne Teilnehmer starten? (Spieler können später trotzdem beitreten – dann „Entsperren & neu zählen“.)')) return;
      }
      
      // Call backend to lock attendance and grant starting coins
      const result = await apiCall('/api/predictions/lock-attendance', 'POST', {
        session_id: sessionId,
        participant_ids: activeIds,
      });
      
      if (result.ok) {
        console.log('Attendance locked:', result);
        // Firestore will be updated by backend, triggers onSnapshot
      } else {
        alert(result.error || 'Fehler beim Sperren');
      }
    } catch (err) {
      console.error('Lock attendance error:', err);
      alert(err.message);
    }
  };
  
  const unlockAttendance = async () => {
    if (!sessionId) return;
    
    try {
      await setDoc(doc(db, "prediction_sessions", sessionId), {
        attendanceLocked: false,
        activeParticipantIds: null,
      }, { merge: true });
    } catch (err) {
      console.error('Unlock attendance error:', err);
    }
  };

  // Host actions
  const startNextUser = async () => {
    // GUARD: never advance when the player order is empty (players not loaded yet /
    // none ready). Prevents accidentally skipping all of Phase A.
    if (sessionUserOrder.length === 0) {
      window.alert("Phase A kann nicht gestartet werden: die Spieler-Reihenfolge ist noch leer (Spieler noch nicht geladen oder keiner spielbereit). Bitte ein paar Sekunden warten und erneut auf „Spiel starten“ tippen – so wird Phase A nicht versehentlich übersprungen.");
      return;
    }
    const nextIndex = (session?.currentUserIndex ?? -1) + 1;
    if (nextIndex >= sessionUserOrder.length) {
      // All users done - Phase A complete, ready for Phase B (Challenges)
      await updatePhase(PHASES.PREDICTIONS_COMPLETE);
      return;
    }
    
    const nextUser = sessionUserOrder[nextIndex];
    // Phase + Audio-Kommando in EINEM Firestore-Write → Stage startet den Song/Video genau einmal
    await updatePhase(PHASES.IDENTITY_GUESS, {
      currentUserIndex: nextIndex,
      currentUserId: nextUser.uid,
      currentPredictionId: null,
      audioState: {
        ...(session?.audioState || { muted: false, volume: 0.7 }),
        playing: !!nextUser.presentation?.songUrl || !!nextUser.presentation?.videoUrl,
        restartNonce: (session?.audioState?.restartNonce || 0) + 1,
        userId: nextUser.uid,
      },
    });
    
    // Start countdown
    await setPhaseTimer(session?.settings?.guessSeconds || 30);
  };

  const closeGuessing = async () => {
    await updatePhase(PHASES.IDENTITY_LOCKED, { phaseEndsAt: null });
  };

  const revealIdentity = async () => {
    await updatePhase(PHASES.IDENTITY_REVEAL);
    
    // Award coins via backend with Firebase ID Token auth (idempotent)
    try {
      const result = await apiCall('/api/predictions/finalize-identity-round', 'POST', {
        session_id: sessionId,
        round_user_id: session.currentUserId,
        // admin_id removed - backend gets UID from verified token
      });
      
      if (result.ok) {
        console.log(`Awarded ${result.total_coins_awarded} coins to ${result.rewarded_count} players`);
      }
    } catch (err) {
      console.error('Failed to finalize identity round:', err);
      // Don't block the UI - coins can be retried
    }
  };

  const startPrediction = async (predictionNumber) => {
    if (!currentUserData) return;
    
    const predType = predictionNumber === 1 ? 'on_court' : 'off_court';
    const pred = currentUserData.predictions.find(p => p.type === predType);
    
    if (!pred) {
      alert(`Keine ${predType === 'on_court' ? 'On-Court' : 'Off-Court'} Prediction gefunden`);
      return;
    }
    
    const phase = predictionNumber === 1 ? PHASES.PREDICTION_1_VOTING : PHASES.PREDICTION_2_VOTING;
    // NO TIMER for probability voting - auto-finalize when all eligible voters voted
    await updatePhase(phase, { 
      currentPredictionId: pred.id,
      phaseEndsAt: null,  // Explicitly no deadline
    });
  };

  const closeVoting = async () => {
    const currentPhase = session?.phase;
    const lockedPhase = currentPhase === PHASES.PREDICTION_1_VOTING 
      ? PHASES.PREDICTION_1_LOCKED 
      : PHASES.PREDICTION_2_LOCKED;
    
    await updatePhase(lockedPhase, { phaseEndsAt: null });
  };

  const revealQuote = async () => {
    if (!currentPrediction) return;
    
    const fromVoting = session?.phase === PHASES.PREDICTION_1_VOTING || session?.phase === PHASES.PREDICTION_2_VOTING;
    const revealPhase = (session?.phase === PHASES.PREDICTION_1_LOCKED || session?.phase === PHASES.PREDICTION_1_VOTING)
      ? PHASES.PREDICTION_1_REVEAL 
      : PHASES.PREDICTION_2_REVEAL;
    
    // Use backend for authoritative quote calculation with Firebase ID Token
    try {
      const result = await apiCall('/api/predictions/finalize-voting', 'POST', {
        session_id: sessionId,
        prediction_id: currentPrediction.id,
      });
      
      if (!result.ok) {
        const msg = result.error || 'Quote konnte nicht berechnet werden';
        if (!window.confirm(`${msg}\n\nTrotzdem weiter zum Reveal (ohne Quote)?`)) return;
        await updatePhase(revealPhase, { phaseEndsAt: null });
        return;
      }
      
      console.log(`Quote: x${result.quote} (${result.teamProbability}%)`);
      
      // Reload prediction to get updated data
      const predSnap = await getDocs(
        query(collection(db, "season_predictions"), where("seasonId", "==", CURRENT_SEASON))
      );
      setPredictions(predSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      
    } catch (err) {
      console.error('Failed to finalize voting:', err);
      if (!window.confirm(`Fehler bei der Quote-Berechnung: ${err.message}\n\nTrotzdem weiter zum Reveal?`)) return;
    }
    
    await updatePhase(revealPhase, { phaseEndsAt: null });
    if (fromVoting) console.log('Voting manually closed by host');
  };

  const finishUser = async () => {
    await updatePhase(PHASES.USER_COMPLETE);
  };

  // ==========================================
  // PHASE NAVIGATION (host can ALWAYS move forward/backward)
  // ==========================================
  const USER_PHASE_ORDER = [
    PHASES.IDENTITY_GUESS, PHASES.IDENTITY_LOCKED, PHASES.IDENTITY_REVEAL,
    PHASES.PREDICTION_1_VOTING, PHASES.PREDICTION_1_REVEAL,
    PHASES.PREDICTION_2_VOTING, PHASES.PREDICTION_2_REVEAL,
    PHASES.USER_COMPLETE,
  ];

  const phaseExtraData = (phase) => {
    if (!currentUserData) return {};
    if (phase === PHASES.PREDICTION_1_VOTING || phase === PHASES.PREDICTION_1_REVEAL) {
      const p = currentUserData.predictions.find(x => x.type === 'on_court');
      return { currentPredictionId: p?.id || null, phaseEndsAt: null };
    }
    if (phase === PHASES.PREDICTION_2_VOTING || phase === PHASES.PREDICTION_2_REVEAL) {
      const p = currentUserData.predictions.find(x => x.type === 'off_court');
      return { currentPredictionId: p?.id || null, phaseEndsAt: null };
    }
    return { phaseEndsAt: null };
  };

  const goPhaseBack = async () => {
    const idx = USER_PHASE_ORDER.indexOf(session?.phase);
    if (idx > 0) {
      await updatePhase(USER_PHASE_ORDER[idx - 1], phaseExtraData(USER_PHASE_ORDER[idx - 1]));
      return;
    }
    if (idx === 0) {
      // back to previous user's summary, or lobby if first user
      const prevIndex = (session?.currentUserIndex ?? 0) - 1;
      if (prevIndex < 0) {
        await updatePhase(PHASES.LOBBY, { currentUserIndex: -1, currentUserId: null, currentPredictionId: null, phaseEndsAt: null });
      } else {
        const prevUser = sessionUserOrder[prevIndex];
        await updatePhase(PHASES.USER_COMPLETE, { currentUserIndex: prevIndex, currentUserId: prevUser?.uid || null, currentPredictionId: null, phaseEndsAt: null });
      }
      return;
    }
    if (session?.phase === PHASES.PREDICTIONS_COMPLETE) {
      const lastIndex = sessionUserOrder.length - 1;
      const lastUser = sessionUserOrder[lastIndex];
      await updatePhase(PHASES.USER_COMPLETE, { currentUserIndex: lastIndex, currentUserId: lastUser?.uid || null, phaseEndsAt: null });
    }
  };

  const goPhaseForward = async () => {
    const p = session?.phase;
    if (p === PHASES.LOBBY) return startNextUser();
    if (p === PHASES.IDENTITY_GUESS) return closeGuessing();
    if (p === PHASES.IDENTITY_LOCKED) return revealIdentity();
    if (p === PHASES.IDENTITY_REVEAL) return startPrediction(1);
    if (p === PHASES.PREDICTION_1_VOTING || p === PHASES.PREDICTION_1_LOCKED) return revealQuote();
    if (p === PHASES.PREDICTION_1_REVEAL) return startPrediction(2);
    if (p === PHASES.PREDICTION_2_VOTING || p === PHASES.PREDICTION_2_LOCKED) return revealQuote();
    if (p === PHASES.PREDICTION_2_REVEAL) return finishUser();
    if (p === PHASES.USER_COMPLETE) return startNextUser();
  };

  const jumpToPhase = async (phase) => {
    await updatePhase(phase, phaseExtraData(phase));
  };

  // ==========================================
  // PHASE B: CHALLENGE GAMES FUNCTIONS
  // ==========================================

  const startChallengePhase = async () => {
    // Initialize Phase B: Set challenge pool from active participants (fallback: joined participants)
    const pool = (session?.activeParticipantIds?.length ? session.activeParticipantIds : participants.map(p => p.userId || p.oduid)).filter(Boolean);
    await setDoc(doc(db, "prediction_sessions", sessionId), {
      phase: 'CHALLENGE_LOBBY',
      challengeParticipantIds: pool,
      challengedUserIds: [],
    }, { merge: true });
  };

  // Toggle leaderboard display on Stage
  const toggleLeaderboardOnStage = async () => {
    try {
      const sessionRef = doc(db, "prediction_sessions", sessionId);
      const currentShowLeaderboard = session?.showLeaderboard || false;
      await updateDoc(sessionRef, {
        showLeaderboard: !currentShowLeaderboard,
      });
    } catch (err) {
      console.error('Toggle leaderboard error:', err);
    }
  };

  const drawChallenge = async () => {
    try {
      const result = await apiCall('/api/challenges/start-round', 'POST', {
        session_id: sessionId,
      });
      
      if (result.ok) {
        console.log(`Challenge drawn: ${result.title}`);
      } else {
        alert(result.error || 'Fehler beim Ziehen der Challenge');
      }
    } catch (err) {
      console.error('Draw challenge error:', err);
      alert(`Fehler: ${err.message}`);
    }
  };

  const spinChallenger = async () => {
    if (!currentChallengeRound?.id) return;
    
    try {
      const result = await apiCall('/api/challenges/spin-challenger', 'POST', {
        session_id: sessionId,
        round_id: currentChallengeRound.id,
      });
      
      if (result.ok) {
        console.log(`Challenger: ${result.challengerDisplayName}`);
      } else {
        alert(result.error || 'Fehler beim Ziehen des Challengers');
      }
    } catch (err) {
      console.error('Spin challenger error:', err);
      alert(`Fehler: ${err.message}`);
    }
  };

  const openBetting = async () => {
    if (!currentChallengeRound?.id) return;
    
    try {
      const result = await apiCall('/api/challenges/open-betting', 'POST', {
        session_id: sessionId,
        round_id: currentChallengeRound.id,
        betting_seconds: bettingTimerSeconds,  // Use selected timer duration
      });
      
      if (result.ok) {
        console.log(`Betting open for ${result.bettingSeconds}s`);
      }
    } catch (err) {
      console.error('Open betting error:', err);
      alert(`Fehler: ${err.message}`);
    }
  };

  const closeBetting = async () => {
    if (!currentChallengeRound?.id) return;
    
    try {
      await apiCall('/api/challenges/close-betting', 'POST', {
        session_id: sessionId,
        round_id: currentChallengeRound.id,
      });
    } catch (err) {
      console.error('Close betting error:', err);
    }
  };

  const updateChallengePool = async () => {
    try {
      await apiCall('/api/challenges/update-challenge-pool', 'POST', {
        session_id: sessionId,
        user_ids: Array.from(challengePoolSelection),
      });
      setShowChallengePoolEditor(false);
    } catch (err) {
      console.error('Update challenge pool error:', err);
      alert(`Fehler: ${err.message}`);
    }
  };

  const drawSecondaryPlayers = async () => {
    if (!currentChallengeRound?.id) return;
    
    const count = currentChallengeRound.secondaryPlayerCount || 0;
    if (count === 0) return;
    
    try {
      const result = await apiCall('/api/challenges/draw-secondary-players', 'POST', {
        session_id: sessionId,
        round_id: currentChallengeRound.id,
        count,
      });
      
      if (result.ok) {
        console.log(`Secondary players: ${result.playerNames.join(', ')}`);
      }
    } catch (err) {
      console.error('Draw secondary players error:', err);
      alert(`Fehler: ${err.message}`);
    }
  };

  const startChallenge = async () => {
    if (!currentChallengeRound?.id) return;
    
    try {
      await apiCall('/api/challenges/start-challenge', 'POST', {
        session_id: sessionId,
        round_id: currentChallengeRound.id,
      });
    } catch (err) {
      console.error('Start challenge error:', err);
    }
  };

  const settleChallenge = async (result) => {
    if (!currentChallengeRound?.id) return;
    
    try {
      const res = await apiCall('/api/challenges/settle', 'POST', {
        session_id: sessionId,
        round_id: currentChallengeRound.id,
        result,
      });
      
      if (res.ok) {
        console.log(`Challenge settled: ${result}, ${res.betsSettled} bets processed`);
        if (res.challengerBonusPaid) {
          console.log(`Challenger bonus paid: +${res.challengerBonus}`);
        }
      }
    } catch (err) {
      console.error('Settle challenge error:', err);
      alert(`Fehler: ${err.message}`);
    }
  };

  // ==========================================
  // END PHASE B FUNCTIONS
  // ==========================================

  const addTime = async (seconds) => {
    if (!session?.phaseEndsAt) return;
    const currentEnd = session.phaseEndsAt.toDate?.() || new Date(session.phaseEndsAt);
    const newEnd = new Date(currentEnd.getTime() + seconds * 1000);
    
    await setDoc(doc(db, "prediction_sessions", sessionId), {
      phaseEndsAt: newEnd,
    }, { merge: true });
  };

  const pauseTimer = async () => {
    await setDoc(doc(db, "prediction_sessions", sessionId), {
      phaseEndsAt: null,
    }, { merge: true });
  };

  // Audio controls – schreiben NUR den Zustand nach Firestore. Einzige Tonquelle: die Stage.
  const stageAudio = session?.audioState || { playing: false, muted: false, volume: 0.7, restartNonce: 0 };
  const setStageAudio = async (patch) => {
    if (!sessionId) return;
    try {
      await setDoc(doc(db, "prediction_sessions", sessionId), {
        audioState: { ...stageAudio, ...patch, userId: session?.currentUserId || null },
      }, { merge: true });
    } catch (e) {
      console.error('Failed to sync audio state:', e);
      alert(`Audio-Befehl konnte nicht an die Stage gesendet werden: ${e.message}`);
    }
  };

  const togglePlay = () => setStageAudio({ playing: !stageAudio.playing });
  const toggleMute = () => setStageAudio({ muted: !stageAudio.muted });
  const restartAudio = () => setStageAudio({ playing: true, restartNonce: (stageAudio.restartNonce || 0) + 1 });
  const updateVolume = (v) => { setVolumeDraft(null); setStageAudio({ volume: Math.max(0, Math.min(1, v)) }); };

  // Stage-Heartbeat prüfen (Stage meldet sich alle 10 s)
  useEffect(() => {
    const check = () => {
      const ts = session?.stageHeartbeatAt;
      const ms = ts?.toMillis?.() ?? (ts ? new Date(ts).getTime() : 0);
      setStageAlive(!!session?.stageActivated && Date.now() - ms < 30000);
    };
    check();
    const iv = setInterval(check, 5000);
    return () => clearInterval(iv);
  }, [session?.stageHeartbeatAt, session?.stageActivated]);

  // Get user name helper
  const getUserName = (uid) => {
    const u = users.find(u => u.id === uid);
    return u?.display_name || u?.name || 'Unknown';
  };

  // Calculate correct guesses
  const correctGuesses = useMemo(() => {
    if (!currentUserData?.presentation?.aliases) return [];
    
    const aliases = currentUserData.presentation.aliases.map(a => a.toLowerCase().trim());
    return guesses.filter(g => {
      const answer = (g.answer || '').toLowerCase().trim();
      return aliases.includes(answer);
    });
  }, [guesses, currentUserData]);

  // Calculate eligible voters and vote status for Probability Voting
  // RULE: eligibleVoters = activeParticipants - ownerUserId (if owner is present)
  const voteStatus = useMemo(() => {
    if (!session || !currentPrediction) {
      return { eligibleCount: 0, votedCount: 0, votedUserIds: [], missingUserIds: [], isComplete: false };
    }
    
    const activeIds = session.activeParticipantIds || [];
    const ownerId = currentPrediction.ownerUserId;
    
    // Eligible voters = active participants minus prediction owner (if present)
    const eligibleVoterIds = activeIds.filter(uid => uid !== ownerId);
    
    // Who has voted?
    const votedUserIds = votes.map(v => v.voterUserId);
    
    // Who is missing?
    const missingUserIds = eligibleVoterIds.filter(uid => !votedUserIds.includes(uid));
    
    return {
      eligibleCount: eligibleVoterIds.length,
      votedCount: votes.length,
      votedUserIds,
      missingUserIds,
      isComplete: votes.length >= eligibleVoterIds.length,
      eligibleVoterIds,
    };
  }, [session, currentPrediction, votes]);

  if (role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0a0608] text-rose-50 flex items-center justify-center">
        <p>Kein Zugriff - nur für Admins</p>
      </div>
    );
  }

  // Session selection / creation
  if (!sessionId || showCreateModal) {
    return (
      <div className="min-h-screen bg-[#0a0608] text-rose-50 pb-24" data-testid="spielmeister-cockpit">
        <header className="px-4 py-3 bg-[#12090c] border-b border-white/5">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin/predictions')} className="p-1">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="font-semibold">🎮 Spielmeister-Cockpit</h1>
              <p className="text-xs text-rose-200/50">HIER steuerst du · die STAGE kommt auf den Beamer</p>
            </div>
          </div>
        </header>

        <div className="px-4 py-6 space-y-6">
          {/* How it works */}
          <div className="grid grid-cols-2 gap-3" data-testid="cockpit-howto">
            <div className="p-4 rounded-xl bg-green-900/20 border border-green-500/30">
              <p className="text-sm font-bold text-green-300">🎮 1. Dieses Fenster = STEUERUNG</p>
              <p className="text-xs text-rose-200/60 mt-1">Bleibt am Laptop. Hier klickst du Anwesenheit, Start, Runden, Reveal.</p>
            </div>
            <div className="p-4 rounded-xl bg-purple-900/20 border border-purple-500/30">
              <p className="text-sm font-bold text-purple-300">📺 2. „Stage öffnen“ = BEAMER</p>
              <p className="text-xs text-rose-200/60 mt-1">Öffnet ein eigenes Fenster → auf den Beamer ziehen → „STAGE AKTIVIEREN“. Fertig.</p>
            </div>
          </div>
          {/* Create new session */}
          <Card className="bg-[#12090c] border-white/10">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Plus size={16} />
                Neue Session erstellen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                value={newSessionName}
                onChange={(e) => setNewSessionName(e.target.value)}
                placeholder="Session Name..."
                className="bg-white/5 border-white/10"
              />
              
              {/* User Selection View */}
              {showUserSelection ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-amber-400">
                      Spieler auswählen ({selectedUserIds.size}/{readyUsers.length})
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={selectAllUsers}
                        className="text-xs px-2 py-1 bg-green-600/20 hover:bg-green-600/30 text-green-400 rounded"
                      >
                        Alle
                      </button>
                      <button
                        onClick={deselectAllUsers}
                        className="text-xs px-2 py-1 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded"
                      >
                        Keine
                      </button>
                    </div>
                  </div>
                  
                  {loadingReadyUsers ? (
                    <p className="text-center text-rose-200/50 py-4">Lade Spieler...</p>
                  ) : readyUsers.length === 0 ? (
                    <p className="text-center text-red-400 py-4">Keine READY Spieler gefunden!</p>
                  ) : (
                    <div className="max-h-60 overflow-y-auto space-y-1 pr-2">
                      {readyUsers.map(user => (
                        <label
                          key={user.user_id}
                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                            selectedUserIds.has(user.user_id) 
                              ? 'bg-green-600/20 border border-green-500/30' 
                              : 'bg-white/5 hover:bg-white/10 border border-transparent'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedUserIds.has(user.user_id)}
                            onChange={() => toggleUserSelection(user.user_id)}
                            className="w-4 h-4 rounded accent-green-500"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{user.display_name}</p>
                            <p className="text-xs text-rose-200/50 truncate">
                              {user.on_court_text?.substring(0, 40)}...
                            </p>
                          </div>
                          <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">
                            READY
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <Button 
                      onClick={() => {
                        setShowUserSelection(false);
                        setSelectedUserIds(new Set());
                      }} 
                      variant="outline"
                      className="border-white/20"
                    >
                      Abbrechen
                    </Button>
                    <Button 
                      onClick={createSession} 
                      className="bg-green-600 hover:bg-green-700"
                      disabled={selectedUserIds.size === 0 || loading}
                    >
                      {loading ? 'Erstelle...' : `Starten (${selectedUserIds.size})`}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <Button onClick={createSession} className="bg-green-600 hover:bg-green-700">
                      <Plus size={14} className="mr-1" />
                      Spieler wählen
                    </Button>
                    <Button onClick={createTestSession} className="bg-amber-600 hover:bg-amber-700">
                      <AlertTriangle size={14} className="mr-1" />
                      Test (1 User)
                    </Button>
                  </div>
                  <p className="text-xs text-rose-200/40">
                    Test-Modus: Erlaubt Session mit nur 1 READY User (nur für Superadmins)
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          {/* Existing sessions */}
          {availableSessions.length > 0 && (
            <Card className="bg-[#12090c] border-white/10">
              <CardHeader>
                <CardTitle className="text-sm">Vorhandene Sessions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {availableSessions.map(sess => (
                  <div key={sess.id} className="flex items-stretch gap-2" data-testid={`session-row-${sess.id}`}>
                    <button
                      data-testid={`control-session-${sess.id}`}
                      onClick={() => navigate(`/admin/predictions/session/${sess.id}`)}
                      className="flex-1 p-3 bg-white/5 hover:bg-white/10 rounded-lg text-left transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{sess.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          sess.phase === PHASES.SESSION_COMPLETE ? 'bg-green-600' :
                          sess.phase === PHASES.LOBBY ? 'bg-blue-600' : 'bg-amber-600'
                        }`}>
                          {sess.phase}
                        </span>
                      </div>
                      <p className="text-xs text-rose-200/50 mt-1">
                        Code {sess.id.slice(-6).toUpperCase()} • {sess.userOrder?.length || 0} User • User {(sess.currentUserIndex ?? -1) + 1}/{sess.userOrder?.length || 0}
                      </p>
                    </button>
                    <button
                      data-testid={`control-btn-${sess.id}`}
                      onClick={() => navigate(`/admin/predictions/session/${sess.id}`)}
                      className="px-3 rounded-lg bg-green-600/20 hover:bg-green-600/40 text-green-200 text-xs font-semibold transition-colors"
                    >
                      🎮 Steuern
                    </button>
                    <button
                      data-testid={`stage-btn-${sess.id}`}
                      onClick={() => openStageWindowFor(sess.id)}
                      className="px-3 rounded-lg bg-purple-600/20 hover:bg-purple-600/40 text-purple-200 text-xs font-semibold transition-colors"
                    >
                      📺 Stage öffnen
                    </button>
                    <button
                      data-testid={`delete-session-${sess.id}`}
                      title="Session löschen"
                      disabled={deletingSessionId === sess.id}
                      onClick={() => deleteSession(sess)}
                      className="px-3 rounded-lg bg-red-600/15 hover:bg-red-600/40 text-red-300 transition-colors disabled:opacity-40"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Spielmeister tools */}
          <Card className="bg-[#12090c] border-amber-500/20">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2 text-amber-400">
                <RotateCcw size={16} /> Rangliste zurücksetzen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-rose-200/50">
                Setzt ALLE Coins auf 0 und löscht Test-Einträge aus der Rangliste. Vor dem echten Spielabend einmal ausführen.
              </p>
              <Button
                data-testid="reset-leaderboard-btn"
                onClick={resetLeaderboard}
                disabled={resettingWallets}
                variant="outline"
                className="w-full bg-amber-600/10 border-amber-500/40 hover:bg-amber-600/30 text-amber-200"
              >
                {resettingWallets ? 'Setze zurück…' : 'Alle Coins auf 0 & Rangliste leeren'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0608] text-rose-50 flex items-center justify-center">
        <p>Laden...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0608] text-rose-50">
      {/* Header */}
      <header className="px-4 py-2 bg-[#12090c] border-b border-white/5 sticky top-0 z-20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin/predictions')} className="p-1">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="font-semibold text-sm flex items-center gap-2">
                <span className="px-2 py-0.5 rounded bg-green-600/30 text-green-300 text-[10px] font-bold tracking-wide" data-testid="host-role-badge">🎮 STEUERUNG</span>
                {session?.name}
              </h1>
              <p className="text-xs text-rose-200/50">
                User {(session?.currentUserIndex ?? -1) + 1}/{sessionUserOrder.length} • {participants.length} Teilnehmer
              </p>
            </div>
          </div>
          
          {/* Stage Link */}
          <div className="flex items-center gap-2">
            <span
              data-testid="stage-alive-badge"
              className={`px-2 py-0.5 rounded text-[10px] font-bold ${stageAlive ? 'bg-green-600/30 text-green-300' : 'bg-red-600/30 text-red-300'}`}
              title={stageAlive ? 'Stage ist aktiviert und meldet sich' : 'Keine aktive Stage – kein Ton/Video auf dem Beamer'}
            >
              {stageAlive ? '📺 STAGE AN' : '📺 KEINE STAGE'}
            </span>
            <Button
              data-testid="open-stage-btn"
              onClick={openStageWindow}
              size="sm"
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Eye size={14} className="mr-1" /> Stage (eigenes Fenster)
            </Button>
            <Button
              onClick={() => toggleLeaderboardOnStage()}
              size="sm"
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              <Trophy size={14} className="mr-1" /> Leaderboard
            </Button>
            <Button
              onClick={openStageWindow}
              size="sm"
              variant="outline"
              className="bg-white/5 border-white/10"
              title="Stage in eigenem Fenster öffnen → auf den Beamer ziehen"
            >
              <Maximize size={14} />
            </Button>
          </div>
        </div>
      </header>

      {/* Phase Display */}
      <div className="px-4 py-3 bg-gradient-to-r from-rose-900/30 to-purple-900/30 border-b border-white/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 bg-white/10 rounded-full text-sm font-medium">
              {session?.phase || 'UNKNOWN'}
            </span>
            {currentUserData && (
              <span className="text-sm">
                {currentUserData.user?.display_name || currentUserData.user?.name}
              </span>
            )}
          </div>
          
          {/* Countdown */}
          {countdown !== null && countdown > 0 && (
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-amber-400" />
              <span className="text-2xl font-mono font-bold text-amber-400">
                {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')}
              </span>
            </div>
          )}
        </div>

        {/* Always-available phase navigation */}
        {session?.phase && !String(session.phase).startsWith('CHALLENGE') && session.phase !== PHASES.SESSION_COMPLETE && (
          <div className="mt-3 flex items-center gap-2" data-testid="phase-nav">
            <Button data-testid="phase-back-btn" onClick={goPhaseBack} size="sm" variant="outline" className="bg-white/5 border-white/15 hover:bg-white/15" disabled={session.phase === PHASES.LOBBY}>
              ◀ Zurück
            </Button>
            <select
              data-testid="phase-jump-select"
              value={USER_PHASE_ORDER.includes(session.phase) ? session.phase : ''}
              onChange={(e) => e.target.value && jumpToPhase(e.target.value)}
              disabled={!currentUserData}
              className="flex-1 h-9 px-2 bg-[#12090c] border border-white/15 rounded-md text-xs"
              title="Direkt zu einer Phase springen"
            >
              <option value="">Phase wählen…</option>
              {USER_PHASE_ORDER.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <Button data-testid="phase-forward-btn" onClick={goPhaseForward} size="sm" className="bg-white/10 hover:bg-white/20" disabled={session.phase === PHASES.LOBBY && !session.attendanceLocked}>
              Weiter ▶
            </Button>
            <Button data-testid="start-phase-two-btn" onClick={startPhaseTwoNow} size="sm" className="bg-amber-600 hover:bg-amber-700 ml-2" title="Prediction-Runden beenden und Mini-Games & Wetten starten">
              🎲 Phase 2: Mini-Games & Wetten
            </Button>
          </div>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* Host Controls - Phase specific */}
        <Card className="bg-[#12090c] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Host Controls</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* LOBBY Phase */}
            {session?.phase === PHASES.LOBBY && (
              <div className="space-y-4">
                {/* Step-by-step */}
                <div className="p-3 rounded-lg bg-purple-900/20 border border-purple-500/30 flex items-center justify-between gap-3" data-testid="lobby-steps">
                  <p className="text-xs text-purple-200/80">
                    <span className="font-bold text-purple-300">Ablauf:</span> ① Stage auf dem Beamer öffnen → ② Spieler treten per Handy bei → ③ Anwesenheit festlegen → ④ Spiel starten
                  </p>
                  <Button data-testid="lobby-open-stage" onClick={openStageWindow} size="sm" className="bg-purple-600 hover:bg-purple-700 shrink-0">
                    📺 Stage öffnen
                  </Button>
                </div>
                {/* Attendance Status */}
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-sm font-medium mb-2">
                    {participants.length} Teilnehmer verbunden
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {participants.map(p => {
                      const user = users.find(u => u.id === (p.userId || p.oduid));
                      return (
                        <div key={p.oduid || p.oduid} className="text-xs text-rose-200/70 flex items-center gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full" />
                          {user?.display_name || user?.name || 'Spieler'}
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Join hint */}
                <div className="p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg text-xs text-blue-200/80 space-y-1" data-testid="join-hint">
                  <p className="font-medium text-blue-300">So kommen die Spieler rein:</p>
                  <p>Handy → RheinStars-App → Predictions → LIVE BEITRETEN, oder QR-Code auf der Stage scannen.</p>
                  <p className="break-all text-blue-300/70">{window.location.origin}/predictions/live/{sessionId}</p>
                </div>
                
                {/* Attendance Lock Status */}
                {session?.attendanceLocked ? (
                  <div className="p-3 bg-green-900/30 border border-green-500/30 rounded-lg">
                    <p className="text-sm text-green-400 font-medium">
                      ✅ Anwesenheit festgelegt ({session?.activeParticipantIds?.length || 0} Spieler)
                    </p>
                    <Button 
                      onClick={unlockAttendance} 
                      variant="outline" 
                      size="sm"
                      className="mt-2 text-xs border-amber-500/30 text-amber-400"
                    >
                      Entsperren & neu zählen
                    </Button>
                  </div>
                ) : (
                  <Button 
                    data-testid="lock-attendance-btn"
                    onClick={lockAttendance} 
                    className="w-full bg-amber-600 hover:bg-amber-700"
                  >
                    🔒 ANWESENHEIT FESTLEGEN {participants.length === 0 ? '(Testlauf ohne Spieler möglich)' : `(${participants.length} Spieler)`}
                  </Button>
                )}
                
                {/* Start Game Button - only if attendance locked */}
                <Button 
                  data-testid="start-game-btn"
                  onClick={startNextUser} 
                  className="w-full bg-green-600 hover:bg-green-700 py-6 text-lg disabled:opacity-50"
                  disabled={!session?.attendanceLocked}
                >
                  <Play size={20} className="mr-2" /> 
                  {session?.attendanceLocked ? 'SPIEL STARTEN' : 'Erst Anwesenheit festlegen'}
                </Button>

                {/* Mini-Games verwalten – schon in der Lobby erreichbar */}
                <Button
                  onClick={() => setShowGamesManager(s => !s)}
                  variant="outline"
                  className="w-full bg-white/5 border-white/10"
                  data-testid="toggle-games-manager-btn"
                >
                  🎮 {showGamesManager ? 'Mini-Games schließen' : 'Mini-Games verwalten (anlegen / auswählen / einsehen)'}
                </Button>
                {showGamesManager && (
                  <div className="mt-2" data-testid="lobby-games-manager">
                    <ChallengeGameMaster sessionId={sessionId} session={session} apiCall={apiCall} playedIds={playedChallengeIds} />
                  </div>
                )}

                {/* Foyer-Hintergrundvideo steuern (wird auf der Stage umgeschaltet) */}
                <div className="flex items-center gap-2 p-2 rounded-lg bg-white/5 border border-white/10">
                  <span className="text-xs text-rose-200/60 mr-auto">🎬 Foyer-Video</span>
                  <Button onClick={() => foyerVideoCmd('prev')} size="sm" variant="outline" className="bg-white/5 border-white/15" data-testid="foyer-prev-btn">⏮ Zurück</Button>
                  <Button onClick={() => foyerVideoCmd('next')} size="sm" variant="outline" className="bg-white/5 border-white/15" data-testid="foyer-next-btn">Weiter ⏭</Button>
                </div>
              </div>
            )}

            {/* IDENTITY_GUESS Phase */}
            {session?.phase === PHASES.IDENTITY_GUESS && (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span>{guesses.length} Antworten</span>
                  <span className="text-green-400">{correctGuesses.length} richtig</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button onClick={() => addTime(10)} variant="outline" className="bg-white/5 border-white/10">
                    <Plus size={14} className="mr-1" /> 10 Sek
                  </Button>
                  <Button onClick={pauseTimer} variant="outline" className="bg-white/5 border-white/10">
                    <Pause size={14} className="mr-1" /> Pause
                  </Button>
                </div>
                <Button onClick={closeGuessing} className="w-full bg-amber-600 hover:bg-amber-700">
                  CLOSE GUESS
                </Button>
              </div>
            )}

            {/* IDENTITY_LOCKED Phase */}
            {session?.phase === PHASES.IDENTITY_LOCKED && (
              <Button onClick={revealIdentity} className="w-full bg-purple-600 hover:bg-purple-700 py-6 text-lg">
                <Eye size={20} className="mr-2" /> REVEAL
              </Button>
            )}

            {/* IDENTITY_REVEAL Phase */}
            {session?.phase === PHASES.IDENTITY_REVEAL && (
              <div className="space-y-3">
                <div className="p-3 bg-green-900/30 rounded-lg">
                  <p className="text-sm font-medium text-green-400">
                    {correctGuesses.length} richtige Antworten → +{correctGuesses.length * (session?.settings?.correctGuessCoins || 10)} Coins
                  </p>
                </div>
                <Button onClick={() => startPrediction(1)} className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg">
                  START PREDICTION 1 (On-Court)
                </Button>
              </div>
            )}

            {/* PREDICTION_1_VOTING Phase - NO TIMER, AUTO-FINALIZE */}
            {session?.phase === PHASES.PREDICTION_1_VOTING && (
              <div className="space-y-3">
                {/* Vote Progress */}
                <div className="p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Abstimmung läuft</span>
                    <span className={`text-lg font-bold ${voteStatus.isComplete ? 'text-green-400' : 'text-amber-400'}`}>
                      {voteStatus.votedCount} / {voteStatus.eligibleCount}
                    </span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2 mb-3">
                    <div 
                      className={`h-2 rounded-full transition-all ${voteStatus.isComplete ? 'bg-green-500' : 'bg-amber-500'}`}
                      style={{ width: `${voteStatus.eligibleCount > 0 ? (voteStatus.votedCount / voteStatus.eligibleCount) * 100 : 0}%` }}
                    />
                  </div>
                  
                  {/* Who's missing - HOST ONLY, before finalize */}
                  {voteStatus.missingUserIds.length > 0 && (
                    <div className="text-xs text-rose-200/50">
                      <p className="mb-1">Fehlt noch:</p>
                      <div className="flex flex-wrap gap-1">
                        {voteStatus.missingUserIds.map(uid => (
                          <span key={uid} className="px-2 py-0.5 bg-red-900/30 text-red-300 rounded">
                            {getUserName(uid)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Auto-finalize hint */}
                {voteStatus.isComplete ? (
                  <div className="p-2 bg-green-900/30 border border-green-500/30 rounded text-center text-sm text-green-400">
                    ✓ Alle haben gestimmt! Voting wird auto-finalisiert.
                  </div>
                ) : (
                  <p className="text-xs text-rose-200/40 text-center">
                    Kein Zeitlimit - Endet wenn alle abgestimmt haben
                  </p>
                )}
                <Button data-testid="close-voting-btn" onClick={revealQuote} className="w-full bg-purple-600 hover:bg-purple-700 py-5 text-base">
                  <Eye size={18} className="mr-2" /> VOTING SCHLIESSEN & QUOTE ZEIGEN
                </Button>
              </div>
            )}

            {/* PREDICTION_1_LOCKED Phase */}
            {session?.phase === PHASES.PREDICTION_1_LOCKED && (
              <Button onClick={revealQuote} className="w-full bg-purple-600 hover:bg-purple-700 py-6 text-lg">
                <Eye size={20} className="mr-2" /> REVEAL QUOTE
              </Button>
            )}

            {/* PREDICTION_1_REVEAL Phase */}
            {session?.phase === PHASES.PREDICTION_1_REVEAL && (
              <Button onClick={() => startPrediction(2)} className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg">
                START PREDICTION 2 (Off-Court)
              </Button>
            )}

            {/* PREDICTION_2_VOTING Phase - NO TIMER, AUTO-FINALIZE */}
            {session?.phase === PHASES.PREDICTION_2_VOTING && (
              <div className="space-y-3">
                {/* Vote Progress */}
                <div className="p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Abstimmung läuft</span>
                    <span className={`text-lg font-bold ${voteStatus.isComplete ? 'text-green-400' : 'text-amber-400'}`}>
                      {voteStatus.votedCount} / {voteStatus.eligibleCount}
                    </span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2 mb-3">
                    <div 
                      className={`h-2 rounded-full transition-all ${voteStatus.isComplete ? 'bg-green-500' : 'bg-amber-500'}`}
                      style={{ width: `${voteStatus.eligibleCount > 0 ? (voteStatus.votedCount / voteStatus.eligibleCount) * 100 : 0}%` }}
                    />
                  </div>
                  
                  {/* Who's missing - HOST ONLY */}
                  {voteStatus.missingUserIds.length > 0 && (
                    <div className="text-xs text-rose-200/50">
                      <p className="mb-1">Fehlt noch:</p>
                      <div className="flex flex-wrap gap-1">
                        {voteStatus.missingUserIds.map(uid => (
                          <span key={uid} className="px-2 py-0.5 bg-red-900/30 text-red-300 rounded">
                            {getUserName(uid)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Auto-finalize hint */}
                {voteStatus.isComplete ? (
                  <div className="p-2 bg-green-900/30 border border-green-500/30 rounded text-center text-sm text-green-400">
                    ✓ Alle haben gestimmt! Voting wird auto-finalisiert.
                  </div>
                ) : (
                  <p className="text-xs text-rose-200/40 text-center">
                    Kein Zeitlimit - Endet wenn alle abgestimmt haben
                  </p>
                )}
                <Button data-testid="close-voting-btn" onClick={revealQuote} className="w-full bg-purple-600 hover:bg-purple-700 py-5 text-base">
                  <Eye size={18} className="mr-2" /> VOTING SCHLIESSEN & QUOTE ZEIGEN
                </Button>
              </div>
            )}

            {/* PREDICTION_2_LOCKED Phase */}
            {session?.phase === PHASES.PREDICTION_2_LOCKED && (
              <Button onClick={revealQuote} className="w-full bg-purple-600 hover:bg-purple-700 py-6 text-lg">
                <Eye size={20} className="mr-2" /> REVEAL QUOTE
              </Button>
            )}

            {/* PREDICTION_2_REVEAL → Zwischenstand */}
            {session?.phase === PHASES.PREDICTION_2_REVEAL && (
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="show-standings-btn" onClick={finishUser} className="bg-amber-600 hover:bg-amber-700 py-6 text-lg">
                  <Trophy size={20} className="mr-2" /> 🪙 ZWISCHENSTAND
                </Button>
                <Button onClick={startNextUser} className="bg-green-600 hover:bg-green-700 py-6 text-lg">
                  <SkipForward size={20} className="mr-2" /> NEXT USER
                </Button>
              </div>
            )}
            {session?.phase === PHASES.USER_COMPLETE && (
              <Button data-testid="next-user-btn" onClick={startNextUser} className="w-full bg-green-600 hover:bg-green-700 py-6 text-lg">
                <SkipForward size={20} className="mr-2" /> NEXT USER
              </Button>
            )}

            {/* PREDICTIONS_COMPLETE Phase - All authors processed, ready for Phase B */}
            {session?.phase === PHASES.PREDICTIONS_COMPLETE && (
              <div className="text-center py-6 space-y-4">
                <CheckCircle size={48} className="mx-auto text-green-500 mb-3" />
                <h3 className="text-lg font-bold text-green-400">Phase A abgeschlossen! 🎉</h3>
                <p className="text-sm text-rose-200/60">
                  Alle {sessionUserOrder.length} User durchgespielt.
                  <br />Alle Predictions haben jetzt Quotes!
                </p>
                <div className="pt-4 border-t border-white/10">
                  <Button 
                    onClick={startChallengePhase}
                    className="w-full bg-amber-600 hover:bg-amber-700 py-4 text-lg"
                  >
                    🎮 PHASE B: CHALLENGES STARTEN
                  </Button>
                </div>
              </div>
            )}

            {/* CHALLENGE_LOBBY Phase - Ready to start challenges */}
            {session?.phase === 'CHALLENGE_LOBBY' && (
              <div className="space-y-4">
                <div className="text-center py-4">
                  <h3 className="text-lg font-bold text-amber-400">🎮 Challenge Games</h3>
                  <p className="text-sm text-rose-200/60 mt-1">
                    {session.challengeParticipantIds?.length || 0} Spieler im Pool
                  </p>
                </div>
                
                {/* Challenge Pool Editor Toggle */}
                <Button 
                  variant="outline"
                  onClick={() => setShowChallengePoolEditor(!showChallengePoolEditor)}
                  className="w-full bg-white/5 border-white/10"
                >
                  {showChallengePoolEditor ? 'Pool-Editor schließen' : '👥 Challenge Pool bearbeiten'}
                </Button>
                
                {showChallengePoolEditor && (
                  <div className="p-3 bg-white/5 rounded-lg max-h-48 overflow-y-auto">
                    <p className="text-xs text-rose-200/60 mb-2">Spieler im Challenge-Pool:</p>
                    {participants.map(p => {
                      const isSelected = challengePoolSelection.has(p.oduid || p.userId);
                      const alreadyChallenged = session.challengedUserIds?.includes(p.oduid || p.userId);
                      return (
                        <label 
                          key={p.id} 
                          className={`flex items-center gap-2 py-1 cursor-pointer ${alreadyChallenged ? 'opacity-50' : ''}`}
                        >
                          <input 
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              const uid = p.oduid || p.userId;
                              const newSet = new Set(challengePoolSelection);
                              if (e.target.checked) {
                                newSet.add(uid);
                              } else {
                                newSet.delete(uid);
                              }
                              setChallengePoolSelection(newSet);
                            }}
                            className="rounded"
                          />
                          <span className="text-sm">{getUserName(p.oduid || p.userId)}</span>
                          {alreadyChallenged && <span className="text-xs text-amber-400">(war Challenger)</span>}
                        </label>
                      );
                    })}
                    <Button 
                      onClick={updateChallengePool}
                      className="w-full mt-3 bg-blue-600 hover:bg-blue-700"
                      size="sm"
                    >
                      Pool speichern ({challengePoolSelection.size} Spieler)
                    </Button>
                  </div>
                )}
                
                <ChallengeGameMaster sessionId={sessionId} session={session} apiCall={apiCall} playedIds={playedChallengeIds} />
              </div>
            )}

            {/* CHALLENGE_DRAWN Phase - Challenge revealed, now spin for challenger */}
            {currentChallengeRound?.phase === 'CHALLENGE_DRAWN' && (
              <div className="space-y-4">
                <div className="p-4 bg-purple-900/20 border border-purple-500/30 rounded-lg">
                  <h4 className="text-xs text-purple-400 uppercase mb-2">Challenge #{currentChallengeRound.roundNumber}</h4>
                  <h3 className="text-xl font-bold">{currentChallengeRound.challengeTitle}</h3>
                  <p className="text-sm text-rose-200/60 mt-2">{currentChallengeRound.challengeDescription}</p>
                </div>
                <Button 
                  onClick={spinChallenger}
                  className="w-full bg-amber-600 hover:bg-amber-700 py-6 text-lg"
                >
                  🎡 CHALLENGER ZIEHEN
                </Button>
              </div>
            )}

            {/* CHALLENGER_DRAWN Phase - Challenger selected, open betting */}
            {currentChallengeRound?.phase === 'CHALLENGER_DRAWN' && (
              <div className="space-y-4">
                <div className="p-4 bg-purple-900/20 border border-purple-500/30 rounded-lg">
                  <h4 className="text-xs text-purple-400 uppercase mb-2">Challenge #{currentChallengeRound.roundNumber}</h4>
                  <h3 className="text-xl font-bold">{currentChallengeRound.challengeTitle}</h3>
                </div>
                <div className="p-4 bg-amber-900/20 border border-amber-500/30 rounded-lg text-center">
                  <p className="text-xs text-amber-400 uppercase mb-1">Challenger</p>
                  <h3 className="text-2xl font-black">{currentChallengeRound.challengerDisplayName}</h3>
                </div>
                
                {/* Timer Duration Selection */}
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-xs text-rose-200/60 mb-2">Betting-Dauer:</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[30, 60, 120, 300, 600].map(secs => (
                      <Button
                        key={secs}
                        variant={bettingTimerSeconds === secs ? 'default' : 'outline'}
                        onClick={() => setBettingTimerSeconds(secs)}
                        className={`text-xs ${bettingTimerSeconds === secs ? 'bg-amber-600' : 'bg-white/5 border-white/10'}`}
                        size="sm"
                      >
                        {secs < 60 ? `${secs}s` : `${secs / 60}m`}
                      </Button>
                    ))}
                    <Input
                      type="number"
                      value={bettingTimerSeconds}
                      onChange={(e) => setBettingTimerSeconds(parseInt(e.target.value) || 60)}
                      className="bg-white/5 border-white/10 text-center text-xs"
                      min={10}
                      max={3600}
                    />
                  </div>
                </div>
                
                <Button 
                  onClick={openBetting}
                  className="w-full bg-green-600 hover:bg-green-700 py-6 text-lg"
                >
                  📊 BETTING ÖFFNEN ({Math.floor(bettingTimerSeconds / 60)}:{(bettingTimerSeconds % 60).toString().padStart(2, '0')})
                </Button>
              </div>
            )}

            {/* BETTING_OPEN Phase - Live betting */}
            {currentChallengeRound?.phase === 'BETTING_OPEN' && (
              <div className="space-y-4">
                <div className="p-4 bg-purple-900/20 border border-purple-500/30 rounded-lg">
                  <h4 className="text-xs text-purple-400 uppercase mb-2">{currentChallengeRound.challengeTitle}</h4>
                  <p className="text-sm text-rose-200/60">Challenger: {currentChallengeRound.challengerDisplayName}</p>
                </div>
                
                {/* Countdown Timer */}
                {bettingCountdown !== null && (
                  <div className={`text-center p-3 rounded-lg ${bettingCountdown <= 10 ? 'bg-red-900/30 border border-red-500/30' : 'bg-amber-900/20 border border-amber-500/30'}`}>
                    <p className="text-xs uppercase text-rose-200/60">Zeit verbleibend</p>
                    <p className={`text-4xl font-black ${bettingCountdown <= 10 ? 'text-red-500 animate-pulse' : 'text-amber-400'}`}>
                      {Math.floor(bettingCountdown / 60)}:{(bettingCountdown % 60).toString().padStart(2, '0')}
                    </p>
                    {bettingCountdown === 0 && (
                      <p className="text-xs text-red-400 mt-1">Timer abgelaufen - wird gesperrt...</p>
                    )}
                  </div>
                )}
                
                {/* Live Odds Display */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-green-900/30 border border-green-500/30 rounded-lg text-center">
                    <p className="text-xs text-green-400 uppercase">JA</p>
                    <p className="text-3xl font-black text-green-400">x{currentChallengeRound.yesOdds?.toFixed(2)}</p>
                    <p className="text-xs text-rose-200/40">{currentChallengeRound.yesStake || 0} Coins</p>
                  </div>
                  <div className="p-4 bg-red-900/30 border border-red-500/30 rounded-lg text-center">
                    <p className="text-xs text-red-400 uppercase">NEIN</p>
                    <p className="text-3xl font-black text-red-400">x{currentChallengeRound.noOdds?.toFixed(2)}</p>
                    <p className="text-xs text-rose-200/40">{currentChallengeRound.noStake || 0} Coins</p>
                  </div>
                </div>
                
                {/* Bet count */}
                <div className="text-center text-sm text-rose-200/60">
                  {challengeBets.length} Wetten platziert
                </div>
                
                <Button 
                  onClick={closeBetting}
                  className="w-full bg-red-600 hover:bg-red-700 py-4"
                >
                  🔒 BETTING SCHLIESSEN
                </Button>
              </div>
            )}

            {/* BETTING_LOCKED Phase - Ready to start challenge */}
            {currentChallengeRound?.phase === 'BETTING_LOCKED' && (
              <div className="space-y-4">
                <div className="p-4 bg-purple-900/20 border border-purple-500/30 rounded-lg">
                  <h4 className="text-xs text-purple-400 uppercase mb-2">{currentChallengeRound.challengeTitle}</h4>
                  <p className="text-sm text-rose-200/60">Challenger: {currentChallengeRound.challengerDisplayName}</p>
                  <p className="text-xs text-amber-400 mt-2">{currentChallengeRound.successCriteria}</p>
                </div>
                
                {/* Draw secondary players if needed */}
                {currentChallengeRound.interactionType === 'RANDOM_TEAMMATES' && 
                 currentChallengeRound.secondaryPlayerCount > 0 &&
                 (!currentChallengeRound.secondaryPlayerIds || currentChallengeRound.secondaryPlayerIds.length === 0) && (
                  <Button 
                    onClick={drawSecondaryPlayers}
                    className="w-full bg-blue-600 hover:bg-blue-700 py-4"
                  >
                    🎲 {currentChallengeRound.secondaryPlayerCount} MITSPIELER ZIEHEN
                  </Button>
                )}
                
                {/* PLAYER_CHOICE: challenger picks a volunteer, host confirms */}
                {currentChallengeRound.interactionType === 'PLAYER_CHOICE' &&
                 (!currentChallengeRound.secondaryPlayerIds || currentChallengeRound.secondaryPlayerIds.length === 0) && (
                  <div className="p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg space-y-2" data-testid="player-choice-box">
                    <p className="text-sm font-bold text-blue-300">{currentChallengeRound.configSnapshot?.settings?.choicePrompt || 'WEN WÄHLT DER CHALLENGER?'}</p>
                    <p className="text-xs text-rose-200/50">Der Challenger sucht sich einen Freiwilligen aus – hier bestätigen:</p>
                    <div className="flex flex-wrap gap-2">
                      {((session?.challengeParticipantIds?.length ? session.challengeParticipantIds : session?.activeParticipantIds) || [])
                        .filter(uid => uid !== currentChallengeRound.challengerUserId)
                        .map(uid => (
                          <button key={uid} data-testid={`choose-${uid}`} onClick={() => setSecondaryPlayers([uid])} className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-blue-600/40 text-sm">
                            {getUserName(uid)}
                          </button>
                        ))}
                    </div>
                  </div>
                )}
                
                {/* Show secondary players if drawn */}
                {currentChallengeRound.secondaryPlayerNames?.length > 0 && (
                  <div className="p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                    <p className="text-xs text-blue-400 uppercase mb-1">Mitspieler</p>
                    <p className="text-sm">{currentChallengeRound.secondaryPlayerNames.join(', ')}</p>
                  </div>
                )}
                
                <Button 
                  onClick={startChallenge}
                  className="w-full bg-amber-600 hover:bg-amber-700 py-6 text-lg"
                >
                  ▶️ CHALLENGE STARTEN
                </Button>
              </div>
            )}

            {/* IN_PROGRESS Phase - Challenge being performed */}
            {currentChallengeRound?.phase === 'IN_PROGRESS' && (
              <div className="space-y-4">
                <div className="p-4 bg-amber-900/30 border border-amber-500/50 rounded-lg text-center">
                  <h3 className="text-xl font-bold text-amber-400">⏳ CHALLENGE LÄUFT</h3>
                  <p className="text-sm text-rose-200/60 mt-2">{currentChallengeRound.challengeTitle}</p>
                  <p className="font-bold mt-1">{currentChallengeRound.challengerDisplayName}</p>
                </div>
                
                <ChallengeLiveTools sessionId={sessionId} round={currentChallengeRound} live={session?.challengeLive} getUserName={getUserName} />
                
                <div className="grid grid-cols-3 gap-2">
                  <Button 
                    onClick={() => settleChallenge('SUCCESS')}
                    className="bg-green-600 hover:bg-green-700 py-6"
                  >
                    ✓ SUCCESS
                  </Button>
                  <Button 
                    onClick={() => settleChallenge('FAIL')}
                    className="bg-red-600 hover:bg-red-700 py-6"
                  >
                    ✗ FAIL
                  </Button>
                  <Button 
                    onClick={() => settleChallenge('VOID')}
                    className="bg-gray-600 hover:bg-gray-700 py-6"
                  >
                    ⊘ VOID
                  </Button>
                </div>
              </div>
            )}

            {/* SETTLED Phase - Show result and next challenge button */}
            {currentChallengeRound?.phase === 'SETTLED' && (
              <div className="space-y-4">
                <div className={`p-6 rounded-lg text-center ${
                  currentChallengeRound.result === 'SUCCESS' ? 'bg-green-900/30 border border-green-500/50' :
                  currentChallengeRound.result === 'FAIL' ? 'bg-red-900/30 border border-red-500/50' :
                  'bg-gray-900/30 border border-gray-500/50'
                }`}>
                  <h3 className="text-3xl font-black">
                    {currentChallengeRound.result === 'SUCCESS' ? '✓ SUCCESS' :
                     currentChallengeRound.result === 'FAIL' ? '✗ FAIL' : '⊘ VOID'}
                  </h3>
                  <p className="text-sm mt-2">{currentChallengeRound.challengerDisplayName}</p>
                </div>
                
                <ChallengeGameMaster sessionId={sessionId} session={session} apiCall={apiCall} playedIds={playedChallengeIds} />
              </div>
            )}

            {/* SESSION_COMPLETE Phase */}
            {session?.phase === PHASES.SESSION_COMPLETE && (
              <div className="text-center py-6">
                <CheckCircle size={48} className="mx-auto text-green-500 mb-3" />
                <h3 className="text-lg font-bold">Session abgeschlossen!</h3>
                <p className="text-sm text-rose-200/60 mt-2">
                  Alle {sessionUserOrder.length} User durchgespielt
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Audio Controls – steuern ausschließlich die Stage */}
        {(currentUserData?.presentation?.songUrl || currentUserData?.presentation?.videoUrl) && (
          <Card className={`bg-[#12090c] ${stageAlive ? 'border-white/10' : 'border-red-500/50'}`} data-testid="host-audio-panel">
            <CardContent className="py-3 space-y-2">
              {!stageAlive && (
                <div className="p-2 rounded-md bg-red-900/40 border border-red-500/40 text-xs text-red-200 flex items-center gap-2" data-testid="stage-offline-warning">
                  <AlertTriangle size={14} className="shrink-0" />
                  <span><b>Keine Stage aktiv</b> – Song/Video wird nicht zu hören sein. Stage öffnen und „STAGE AKTIVIEREN“ klicken.</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs text-rose-200/60 leading-tight">
                  <p className="font-medium text-rose-100">🔊 Ton läuft nur auf der Stage</p>
                  <p data-testid="stage-audio-status">
                    {stageAlive ? <span className="text-green-400">● Stage verbunden</span> : <span className="text-red-400">○ Stage offline</span>}
                    {' · '}{stageAudio.playing ? 'spielt' : 'pausiert'}{stageAudio.muted ? ' · stumm' : ''}{' · '}{Math.round((stageAudio.volume ?? 0.7) * 100)}%
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button data-testid="stage-audio-restart" onClick={restartAudio} size="sm" variant="outline" className="bg-white/5 border-white/10" title="Von vorne (auf der Stage)">
                    <RotateCcw size={14} />
                  </Button>
                  <Button data-testid="stage-audio-toggle" onClick={togglePlay} size="sm" variant="outline" className="bg-white/5 border-white/10" title={stageAudio.playing ? 'Stage pausieren' : 'Stage abspielen'}>
                    {stageAudio.playing ? <Pause size={14} /> : <Play size={14} />}
                  </Button>
                  <Button data-testid="stage-audio-mute" onClick={toggleMute} size="sm" variant="outline" className="bg-white/5 border-white/10" title={stageAudio.muted ? 'Stage laut' : 'Stage stumm'}>
                    {stageAudio.muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                  </Button>
                  <input
                    data-testid="stage-audio-volume"
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={volumeDraft ?? stageAudio.volume ?? 0.7}
                    onChange={(e) => setVolumeDraft(parseFloat(e.target.value))}
                    onMouseUp={(e) => updateVolume(parseFloat(e.target.value))}
                    onTouchEnd={(e) => updateVolume(parseFloat(e.target.value))}
                    onKeyUp={(e) => updateVolume(parseFloat(e.target.value))}
                    className="w-24"
                    title="Stage-Lautstärke"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current User Info */}
        {currentUserData && (
          <Card className="bg-[#12090c] border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Aktueller User</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                {currentUserData.presentation?.backgroundImageUrl && (
                  <img 
                    src={currentUserData.presentation.backgroundImageUrl} 
                    alt="" 
                    className="w-24 h-24 object-cover rounded-lg"
                  />
                )}
                <div className="flex-1">
                  <h3 className="font-bold">{currentUserData.user?.display_name || currentUserData.user?.name}</h3>
                  <p className="text-xs text-rose-200/50 mt-1">
                    Aliases: {currentUserData.presentation?.aliases?.join(', ')}
                  </p>
                  <div className="mt-2 space-y-1">
                    {currentUserData.predictions.map(p => (
                      <p key={p.id} className="text-xs">
                        <span className={`inline-block w-2 h-2 rounded mr-1 ${p.type === 'on_court' ? 'bg-blue-500' : 'bg-purple-500'}`} />
                        {p.text?.substring(0, 50)}...
                        {p.quote && <span className="ml-2 text-amber-400">x{p.quote.toFixed(2)}</span>}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Participants */}
        <Card className="bg-[#12090c] border-white/10" data-testid="participants-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between">
              <span className="flex items-center gap-2"><Users size={16} /> Teilnehmer ({participants.length})</span>
              <span className="text-xs text-amber-300/80 font-normal">🪙 Coins direkt korrigierbar</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {participants.length === 0 ? (
              <p className="text-xs text-rose-200/50">Noch niemand beigetreten.</p>
            ) : (
              <div className="space-y-1">
                {participants.map(p => {
                  const participantId = p.userId || p.oduid;
                  const bal = wallets[participantId]?.balance ?? 0;
                  const isEditing = coinEdit.userId === participantId;
                  return (
                    <div key={participantId} data-testid={`participant-${participantId}`} className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg">
                      <span className="flex-1 text-sm font-medium truncate">{getUserName(participantId)}</span>
                      <span className="text-sm font-bold text-amber-300 tabular-nums w-16 text-right" data-testid={`balance-${participantId}`}>{bal} 🪙</span>
                      <button data-testid={`coin-minus-${participantId}`} onClick={() => adjustCoins(participantId, -10)} disabled={coinBusy} className="px-2 py-1 rounded bg-red-600/20 hover:bg-red-600/40 text-red-200 text-xs disabled:opacity-40">−10</button>
                      <button data-testid={`coin-plus-${participantId}`} onClick={() => adjustCoins(participantId, 10)} disabled={coinBusy} className="px-2 py-1 rounded bg-green-600/20 hover:bg-green-600/40 text-green-200 text-xs disabled:opacity-40">+10</button>
                      {isEditing ? (
                        <form
                          className="flex items-center gap-1"
                          onSubmit={(e) => { e.preventDefault(); const v = parseInt(coinEdit.value, 10); if (!isNaN(v) && v !== 0) adjustCoins(participantId, v); setCoinEdit({ userId: null, value: '' }); }}
                        >
                          <input
                            data-testid={`coin-input-${participantId}`}
                            autoFocus
                            type="number"
                            value={coinEdit.value}
                            onChange={(e) => setCoinEdit({ userId: participantId, value: e.target.value })}
                            placeholder="±Coins"
                            className="w-20 h-7 px-2 bg-[#0a0608] border border-white/20 rounded text-xs"
                          />
                          <button type="submit" data-testid={`coin-apply-${participantId}`} className="px-2 py-1 rounded bg-amber-600/40 hover:bg-amber-600/60 text-xs">OK</button>
                          <button type="button" onClick={() => setCoinEdit({ userId: null, value: '' })} className="px-1 text-xs text-rose-200/50">✕</button>
                        </form>
                      ) : (
                        <button data-testid={`coin-edit-${participantId}`} onClick={() => setCoinEdit({ userId: participantId, value: '' })} className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-xs" title="Beliebigen Betrag gutschreiben/abziehen">±</button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* User Order */}
        <Card className="bg-[#12090c] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Reihenfolge</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {sessionUserOrder.map((u, idx) => (
                <div 
                  key={u.uid}
                  className={`flex items-center gap-2 p-2 rounded ${
                    idx === session?.currentUserIndex ? 'bg-rose-600/30' :
                    idx < (session?.currentUserIndex ?? -1) ? 'bg-green-900/20 text-rose-200/50' : 'bg-white/5'
                  }`}
                >
                  <span className="text-xs w-6">{idx + 1}.</span>
                  <span className="flex-1 text-sm">{u.user?.display_name || u.user?.name}</span>
                  {idx < (session?.currentUserIndex ?? -1) && <CheckCircle size={14} className="text-green-500" />}
                  {idx === session?.currentUserIndex && <Play size={14} className="text-amber-400" />}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
